
import { useState, useEffect } from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import {
  fetchStaffManagedUsers,
  createUserByStaff,
  updateUserByStaff,
  assignCourseToUserByStaff,
  unassignCourseFromUserByStaff,
  getUserCourseAssignmentsByStaff,
  getUserAnalyticsByStaff,
  getStaffActionLogs,
  bulkAssignCoursesToUsersByStaff,
  StaffManagedUser,
  StaffUserManagementLog,
  CreateUserData
} from './staffUserManagement';

export const useStaffUserManagement = () => {
  const { hasAccess } = useSupabaseAuth();
  const [users, setUsers] = useState<StaffManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLogs, setActionLogs] = useState<StaffUserManagementLog[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Check if current user has staff access
  const hasStaffAccess = hasAccess(['staff', 'admin']);

  const loadUsers = async () => {
    if (!hasStaffAccess) {
      console.log('User does not have staff access');
      setUsers([]);
      setLoading(false);
      setError(null);
      return;
    }

    console.log('Loading users for staff management...');
    setLoading(true);
    setError(null);
    
    try {
      const staffUsers = await fetchStaffManagedUsers();
      console.log('Loaded users:', staffUsers);
      setUsers(staffUsers);
      
      // If no users found, it's not an error - just an empty state
      if (staffUsers.length === 0) {
        console.log('No users found - this is normal for new systems');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadActionLogs = async (targetUserId?: string) => {
    if (!hasStaffAccess) return;
    
    try {
      const logs = await getStaffActionLogs(targetUserId);
      setActionLogs(logs);
    } catch (err) {
      console.error('Error loading action logs:', err);
    }
  };

  const createUser = async (userData: CreateUserData): Promise<boolean> => {
    if (!hasStaffAccess) return false;
    
    try {
      console.log('Creating user via staff management:', userData);
      const success = await createUserByStaff(userData);
      if (success) {
        console.log('User created successfully, refreshing user list');
        await loadUsers(); // Refresh users list
        await loadActionLogs(); // Refresh logs
      }
      return success;
    } catch (err) {
      console.error('Error creating user:', err);
      return false;
    }
  };

  const updateUser = async (
    userId: string, 
    updates: { name?: string; email?: string; is_approved?: boolean }
  ): Promise<boolean> => {
    if (!hasStaffAccess) return false;
    
    try {
      const success = await updateUserByStaff(userId, updates);
      if (success) {
        await loadUsers(); // Refresh users list
        await loadActionLogs(); // Refresh logs
      }
      return success;
    } catch (err) {
      console.error('Error updating user:', err);
      return false;
    }
  };

  const assignCourse = async (userId: string, courseId: string): Promise<boolean> => {
    if (!hasStaffAccess) return false;
    
    try {
      const success = await assignCourseToUserByStaff(userId, courseId, 'manual');
      if (success) {
        await loadActionLogs(); // Refresh logs
      }
      return success;
    } catch (err) {
      console.error('Error assigning course:', err);
      return false;
    }
  };

  const unassignCourse = async (userId: string, courseId: string): Promise<boolean> => {
    if (!hasStaffAccess) return false;
    
    try {
      const success = await unassignCourseFromUserByStaff(userId, courseId);
      if (success) {
        await loadActionLogs(); // Refresh logs
      }
      return success;
    } catch (err) {
      console.error('Error unassigning course:', err);
      return false;
    }
  };

  const getUserCourseAssignments = async (userId: string) => {
    if (!hasStaffAccess) return [];
    
    try {
      return await getUserCourseAssignmentsByStaff(userId);
    } catch (err) {
      console.error('Error getting user course assignments:', err);
      return [];
    }
  };

  const getUserAnalytics = async (userId: string) => {
    if (!hasStaffAccess) return null;
    
    try {
      return await getUserAnalyticsByStaff(userId);
    } catch (err) {
      console.error('Error getting user analytics:', err);
      return null;
    }
  };

  const bulkAssignCourses = async (userIds: string[], courseIds: string[]): Promise<boolean> => {
    if (!hasStaffAccess) return false;
    
    try {
      const success = await bulkAssignCoursesToUsersByStaff(userIds, courseIds);
      if (success) {
        await loadActionLogs(); // Refresh logs
      }
      return success;
    } catch (err) {
      console.error('Error bulk assigning courses:', err);
      return false;
    }
  };

  const refreshData = async () => {
    await loadUsers();
    await loadActionLogs();
  };

  // Load users on mount and when staff access changes
  useEffect(() => {
    loadUsers();
  }, [hasStaffAccess]);

  return {
    // State
    users,
    loading,
    actionLogs,
    error,
    hasStaffAccess,
    
    // User management
    loadUsers,
    createUser,
    updateUser,
    
    // Course management
    assignCourse,
    unassignCourse,
    getUserCourseAssignments,
    bulkAssignCourses,
    
    // Analytics
    getUserAnalytics,
    
    // Logs
    loadActionLogs,
    
    // Utility
    refreshData
  };
};
